import { FC, FormEvent, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { DataTable, Dialog, SearchableSelect, useDialog } from 'components';
import { TextField } from 'components/inputs/text-field/text-field';
import { DataTableRef } from 'components/tables/data-table';
import { useAuth } from 'contexts';
import useConfirm from 'hooks/use-confirm';
import { ReservationUnit, useReservation } from 'hooks/use-reservation';
import unitDataColumns, { defaultUnitColumns } from 'pages/units/grid/unit-columns';
import { downloadUngroupedCsv, fetchActiveUnitsUngrouped } from 'services';
import { ActiveUnit } from 'types';
import useRedeliveryApi from './use-redelivery-api';

import { RedeliveryContext } from './redelivery-context';

const RedeliveryUnits: FC = () => {
  const { userProfile } = useAuth();
  const { customer, location, id: redeliveryId, redeliveryDetails, redeliveryNumber } = useContext(RedeliveryContext);

  const { getConfirmation, ConfirmationDialog } = useConfirm();
  const { closeDialog, openDialog, ref: dialogRef } = useDialog();
  const { reserveUnit, unreserveUnit } = useRedeliveryApi();

  const fetchData = useMemo(() => fetchActiveUnitsUngrouped({ redeliveryId }), [redeliveryId]);
  const fetchCsv = useMemo(() => downloadUngroupedCsv({ redeliveryId }), [redeliveryId]);

  const dataTableRef = useRef<DataTableRef>(null);

  const userColumns = useMemo(() => {
    return userProfile.columnPreferences
      .filter(({ table }) => table === 'UNIT_UNSTACKED')
      .map(({ column, width }) => ({ field: column, width }));
  }, [userProfile.columnPreferences]);

  const initialColumns = useMemo(() => {
    return userColumns.length ? userColumns : defaultUnitColumns.map(field => ({ field, width: 80 }));
  }, [userColumns]);

  const sizeTypeOptions = useMemo(() => {
    return [...redeliveryDetails.values()].map(({ sizeType }) => ({ label: sizeType, value: sizeType }));
  }, [redeliveryDetails]);

  const [unitToReserve, setUnitToReserve] = useState<ReservationUnit>({});

  const { handleSetUnit, validateUnitNumber } = useReservation({
    customerId: customer?.id,
    locationId: location?.id,
    redeliveryDetails: [...redeliveryDetails.values()],
    setUnit: setUnitToReserve
  });
  const setUnitNumber = useCallback((unitNumber: string) => handleSetUnit(undefined, unitNumber), [handleSetUnit]);
  const setSizeType = useCallback((sizeType: string) => handleSetUnit(sizeType), [handleSetUnit]);

  const handleReset = useCallback(() => {
    setUnitToReserve({});
    closeDialog();
  }, [closeDialog, setUnitToReserve]);

  /* prettier-ignore */
  const handleReserve = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!redeliveryId) return;
    const response = await reserveUnit(redeliveryId, unitToReserve);
    if (!response) return;
    dataTableRef.current?.forceFetch();
    handleReset();
  }, [redeliveryId, unitToReserve, handleReset, reserveUnit]);

  /* prettier-ignore */
  const handleUnreserve = useCallback((row: ActiveUnit) => async () => {
    if (!redeliveryId) return;
    const confirm = await getConfirmation(
      'Unreserve',
      `Remove unit #${row.unitNumber} from redelivery #${redeliveryNumber}?`
    );
    if (!confirm) return;
    const response = await unreserveUnit(redeliveryId, row);
    if (response) dataTableRef.current?.forceFetch();
  }, [getConfirmation, redeliveryId, redeliveryNumber, unreserveUnit]);

  return !redeliveryId ? null : (
    <div className="card-primary">
      <div className="card-header">
        <h4>UNIT DETAILS</h4>
        <button className="btn-primary" data-cy="redelivery-add-unit-button" onClick={openDialog}>
          ADD UNIT
        </button>
      </div>
      <div className="card-body">
        <ConfirmationDialog danger />
        <DataTable
          data-cy="redelivery-units"
          ref={dataTableRef}
          fetchData={fetchData}
          fetchCsv={fetchCsv}
          columns={unitDataColumns}
          initialColumns={initialColumns}
          initialPage={{ pageNum: 0, pageSize: 5 }}
          pageSizeOptions={[5, 25, 50]}
          getRowActions={(row: ActiveUnit) => [
            <button
              className="btn-back"
              data-cy="unreserve-unit-button"
              disabled={!!row.deliveredDate}
              onClick={handleUnreserve(row)}
            >
              <span className="material-icons icon-large">cancel</span>
            </button>
          ]}
          noRowsMessage={<i>NO UNITS ASSIGNED</i>}
        />
        <Dialog ref={dialogRef} closeDialog={closeDialog} data-cy="redelivery-add-unit-dialog">
          <form className="card-primary" data-cy="reserve-unit-form" onReset={handleReset} onSubmit={handleReserve}>
            <div className="card-header">
              <h4>RESERVE UNIT</h4>
              <div className="grid-2">
                <button type="reset" className="btn-outline">
                  cancel
                </button>
                <button type="submit" className="btn-primary">
                  submit
                </button>
              </div>
            </div>
            <div className="card-body grid-2" style={{ padding: 12 }}>
              <TextField
                data-cy="unit-number-input"
                required
                label="UNIT NUMBER"
                value={unitToReserve.unitNumber ?? ''}
                onChange={setUnitNumber}
                validate={validateUnitNumber}
              />
              <SearchableSelect
                containerClass="text-field"
                data-cy="size-type-select"
                inputClass={!unitToReserve.sizeType ? 'blank' : ''}
                label="SIZE TYPE"
                onValueSelected={setSizeType}
                options={sizeTypeOptions}
                value={unitToReserve.sizeType ?? ''}
                required
              />
            </div>
          </form>
        </Dialog>
      </div>
    </div>
  );
};

export default RedeliveryUnits;
